import axios, { AxiosInstance } from 'axios';

// API Configuration
const DEFAULT_API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.dev.mettastay.com/api/v4';
const API_DELAY = 300; // Simulated network delay in ms for mock mode

// Toggle between mock and real API
export const USE_MOCK = false;

// Business details type
export interface BusinessDetails {
  user_id?: string;
  org_name?: string;
  [key: string]: string | number | boolean | null | undefined;
}

// Simulate API delay for mock responses
export const delay = (ms: number = API_DELAY) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Create axios instance with custom or default base URL
 */
function createApiClient(baseURL?: string): AxiosInstance {
  const apiClient = axios.create({
    baseURL: baseURL || DEFAULT_API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
    },
  });
  
  // Handle ngrok URLs
  if ((baseURL || DEFAULT_API_BASE_URL).includes('ngrok')) {
    apiClient.defaults.headers['ngrok-skip-browser-warning'] = 'true';
  }
  
  return apiClient;
}

// Export singleton axios instance
export const apiClient = createApiClient();

// Add request interceptor to check authentication
apiClient.interceptors.request.use(
  (config) => {
    // Skip authentication check for mock mode or currentuser endpoint
    if (USE_MOCK) {
      return config;
    }
    
    const isCurrentUserEndpoint = config.url?.includes('/currentuser');
    if (isCurrentUserEndpoint) {
      return config;
    }
    
    // Check if both auth token and business details are present in the default headers
    const hasAuthToken = !!apiClient.defaults.headers.common['Authorization'];
    const hasBusinessDetails = !!apiClient.defaults.headers.common['business_details'];
    
    // Debug logging to help identify the issue
    // console.log('API Request Interceptor:', {
    //   url: config.url,
    //   hasAuthToken,
    //   hasBusinessDetails,
    //   requestHeaders: config.headers,
    //   defaultHeaders: apiClient.defaults.headers.common,
    //   authorizationDefault: apiClient.defaults.headers.common['Authorization'],
    //   businessDetailsDefault: apiClient.defaults.headers.common['business_details'],
    //   // Show first 50 chars of token for debugging
    //   authTokenPreview: String(apiClient.defaults.headers.common['Authorization'] || '').substring(0, 50) + '...',
    //   businessDetailsPreview: String(apiClient.defaults.headers.common['business_details'] || '').substring(0, 100) + '...'
    // });
    
    if (!hasAuthToken || !hasBusinessDetails) {
      const missingAuth = [];
      if (!hasAuthToken) missingAuth.push('auth token');
      if (!hasBusinessDetails) missingAuth.push('business details');
      
      const error = new Error(
        `Authentication required: Missing ${missingAuth.join(' and ')}. ` +
        'API calls are blocked until both auth token and business details are provided.'
      );
      
      // Log the error for debugging
      console.error('API Request Blocked:', error.message);
      
      // Reject the request to prevent the API call
      return Promise.reject(error);
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Set auth token for API requests
 */
export function setAuthToken(token: string) {
  apiClient.defaults.headers.common['Authorization'] = token;
}

/**
 * Set business details header for API requests
 */
export function setBusinessDetails(details: BusinessDetails | string) {
  const businessDetailsHeader = typeof details === 'string' 
    ? details 
    : JSON.stringify(details);
  apiClient.defaults.headers.common['business_details'] = businessDetailsHeader;
}

/**
 * Remove auth token
 */
export function clearAuthToken() {
  delete apiClient.defaults.headers.common['Authorization'];
}

/**
 * Remove business details header
 */
export function clearBusinessDetails() {
  delete apiClient.defaults.headers.common['business_details'];
}

/**
 * Check if both auth token and business details are present
 */
export function hasValidAuthentication(): boolean {
  const hasAuthToken = !!apiClient.defaults.headers.common['Authorization'];
  const hasBusinessDetails = !!apiClient.defaults.headers.common['business_details'];
  return hasAuthToken && hasBusinessDetails;
}

/**
 * Get authentication status details
 */
export function getAuthenticationStatus(): {
  hasAuthToken: boolean;
  hasBusinessDetails: boolean;
  isAuthenticated: boolean;
  missing: string[];
} {
  const hasAuthToken = !!apiClient.defaults.headers.common['Authorization'];
  const hasBusinessDetails = !!apiClient.defaults.headers.common['business_details'];
  const missing = [];
  
  if (!hasAuthToken) missing.push('auth token');
  if (!hasBusinessDetails) missing.push('business details');
  
  return {
    hasAuthToken,
    hasBusinessDetails,
    isAuthenticated: hasAuthToken && hasBusinessDetails,
    missing
  };
}

/**
 * Clear all auth-related headers
 */
export function clearAuth() {
  clearAuthToken();
  clearBusinessDetails();
}

// Generic API response type
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
}

// Generic paginated response
export interface PaginatedResponse<T> extends ApiResponse<T[]> {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// API Endpoints - centralized endpoint definitions
export const API_ENDPOINTS = {
  // Properties
  properties: {
    list: '/hotel_branch/revenue_manager_branch_list',
    get: (id: string) => `/properties/${id}`,
    create: '/properties',
    update: (id: string) => `/properties/${id}`,
    delete: (id: string) => `/properties/${id}`,
  },
  
  // Room Inventory
  roomInventory: {
    list: (propertyId: string) => `/properties/${propertyId}/room-inventory`,
    get: (propertyId: string, roomTypeId: string) => `/properties/${propertyId}/room-inventory/${roomTypeId}`,
    create: (propertyId: string) => `/properties/${propertyId}/room-inventory`,
    update: (propertyId: string, roomTypeId: string) => `/properties/${propertyId}/room-inventory/${roomTypeId}`,
    delete: (propertyId: string, roomTypeId: string) => `/properties/${propertyId}/room-inventory/${roomTypeId}`,
    roomTypes: '/hotel_room_types?',
    roomDataTodayByBranch: '/hotel_room_types/roomDataTodayByBranch',
  },
  
  // Pricing Rules
  pricingRules: {
    list: (propertyId: string) => `/properties/${propertyId}/pricing-rules`,
    get: (propertyId: string, ruleId: string) => `/properties/${propertyId}/pricing-rules/${ruleId}`,
    create: (propertyId: string) => `/properties/${propertyId}/pricing-rules`,
    update: (propertyId: string, ruleId: string) => `/properties/${propertyId}/pricing-rules/${ruleId}`,
    delete: (propertyId: string, ruleId: string) => `/properties/${propertyId}/pricing-rules/${ruleId}`,
    toggle: (propertyId: string, ruleId: string) => `/properties/${propertyId}/pricing-rules/${ruleId}/toggle`,
    run: (propertyId: string, ruleId: string) => `/properties/${propertyId}/pricing-rules/${ruleId}/run`,
    import: (propertyId: string) => `/properties/${propertyId}/pricing-rules/import`,
    export: (propertyId: string) => `/properties/${propertyId}/pricing-rules/export`,
  },
  
  // Availability Rules
  availabilityRules: {
    list: (propertyId: string) => `/properties/${propertyId}/availability-rules`,
    get: (propertyId: string, ruleId: string) => `/properties/${propertyId}/availability-rules/${ruleId}`,
    create: (propertyId: string) => `/properties/${propertyId}/availability-rules`,
    update: (propertyId: string, ruleId: string) => `/properties/${propertyId}/availability-rules/${ruleId}`,
    delete: (propertyId: string, ruleId: string) => `/properties/${propertyId}/availability-rules/${ruleId}`,
    toggle: (propertyId: string, ruleId: string) => `/properties/${propertyId}/availability-rules/${ruleId}/toggle`,
    run: (propertyId: string, ruleId: string) => `/properties/${propertyId}/availability-rules/${ruleId}/run`,
    import: (propertyId: string) => `/properties/${propertyId}/availability-rules/import`,
    export: (propertyId: string) => `/properties/${propertyId}/availability-rules/export`,
  },
  
  // Revenue Rules - Unified endpoint for both pricing and availability rules
  revenueRules: {
    create: '/revenue/rules',
    update: (ruleId: string) => `/revenue/rules/${ruleId}`,
    delete: (ruleId: string) => `/revenue/rules/${ruleId}`,
    get: (ruleId: string) => `/revenue/rules/${ruleId}`,
    list: '/revenue/rules',
    import: '/revenue/rules/import',
  },

  // Generic API endpoint for querying resources
  generic: '/generic',
  
  // Events
  events: {
    list: '/revenue/events',
    listByProperty: (propertyId: string) => `/revenue/events?propertyId=${propertyId}`,
    get: (eventId: string) => `/revenue/events/${eventId}`,
    create: '/revenue/events',
    update: (eventId: string) => `/revenue/events/${eventId}`,
    delete: (eventId: string) => `/revenue/events/${eventId}`,
    import: '/revenue/events/import',
    export: (propertyId: string) => `/revenue/events/export?propertyId=${propertyId}`,
  },
  
  // OTA Channels
  otaChannels: {
    list: '/ota-channels',
    get: (channelId: string) => `/ota-channels/${channelId}`,
    create: '/ota-channels',
    update: (channelId: string) => `/ota-channels/${channelId}`,
    delete: (channelId: string) => `/ota-channels/${channelId}`,
  },
  
  // Calendar
  calendar: {
    getAvailability: (propertyId: string, startDate: string, endDate: string) => 
      `/properties/${propertyId}/calendar?start=${startDate}&end=${endDate}`,
    getData: (propertyId: string, year: number, month: number) =>
      `/properties/${propertyId}/calendar/${year}/${month}`,
    updateDate: (propertyId: string, date: string) => `/properties/${propertyId}/calendar/${date}`,
    bulkUpdate: (propertyId: string) => `/properties/${propertyId}/calendar/bulk`,
  },
  
  // Analytics
  analytics: {
    overview: (propertyId: string) => `/properties/${propertyId}/analytics/overview`,
    revenue: (propertyId: string, startDate: string, endDate: string) => 
      `/properties/${propertyId}/analytics/revenue?start=${startDate}&end=${endDate}`,
    occupancy: (propertyId: string, startDate: string, endDate: string) => 
      `/properties/${propertyId}/analytics/occupancy?start=${startDate}&end=${endDate}`,
    rulePerformance: (propertyId: string) => `/properties/${propertyId}/analytics/rules`,
    applyInsight: (propertyId: string, insightId: string) => 
      `/properties/${propertyId}/analytics/insights/${insightId}/apply`,
    appliedInsights: (propertyId: string) => `/properties/${propertyId}/analytics/insights/applied`,
  },
  
  // User
  user: {
    getCurrentUser: '/currentuser',
  },
};

// Type definitions for API requests/responses
export interface PropertyRequest {
  name: string;
  location: string;
  basePrice: number;
  roomCount: number;
}

export interface PricingRuleRequest {
  name: string;
  priority: number;
  isActive: boolean;
  conditions: Array<{
    type: 'daysUntilCheckIn' | 'occupancy' | 'dayOfWeek' | 'event';
    operator: 'equals' | 'greaterThan' | 'lessThan' | 'greaterThanOrEqual' | 'lessThanOrEqual';
    value: number | string;
    logic?: 'AND' | 'OR';
  }>;
  adjustmentType: 'percentage' | 'fixed';
  adjustmentValue: number;
  roomTypes: string[];
  otaChannels: string[];
  startDate: string;
  endDate?: string;
  daysOfWeek?: number[];
  timeOfDay?: string;
  processingFrequency: 'manual' | 'immediate' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'specific';
  executionDays?: number[];
  executionTime?: string;
  executionEndDate?: string;
}

export interface AvailabilityRuleRequest {
  name: string;
  priority: number;
  isActive: boolean;
  description?: string;
  roomTypes: string[];
  otaChannels: string[];
  actionType: 'setStatus' | 'setMinStay' | 'setMaxStay';
  actionValue: string | number;
  startDate: string;
  endDate?: string;
  daysOfWeek?: number[];
  timeOfDay?: string;
  processingFrequency: 'manual' | 'immediate' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'specific';
  executionDays?: number[];
  executionTime?: string;
  executionEndDate?: string;
}

export interface EventRequest {
  name: string;
  startDate: string;
  endDate: string;
  category: 'conference' | 'festival' | 'holiday' | 'sports' | 'other';
  impact: 'low' | 'medium' | 'high';
  description?: string;
  pricingMultiplier: number;
  propertyIds: string[];
}

export interface ImportRulesRequest {
  targetPropertyIds: string[];
  rules: PricingRuleRequest[] | AvailabilityRuleRequest[];
}
